import "./Text.scss";

import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

/**
 * Typography tokens — the single source of truth for text styles. Each variant
 * maps to one `.text--<variant>` class in `Text.scss`, backed by the CSS custom
 * properties in `tokens.scss`. Prefer this component (or the `text--<variant>`
 * class on a non-text element) over raw `font-*` declarations.
 */
export type TextVariant =
  | "display" // large page/hero heading (responsive)
  | "title" // section / page title
  | "body-lg" // emphasised body
  | "body-strong" // semibold body
  | "body" // default running text
  | "label" // buttons, form labels, dense UI
  | "caption"; // small helper text

type TextOwnProps<T extends ElementType> = {
  /** Element to render. Defaults to `"p"`. */
  as?: T;
  /** Typography token. Defaults to `"body"`. */
  variant?: TextVariant;
  className?: string;
  children: ReactNode;
};

type TextProps<T extends ElementType> = TextOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof TextOwnProps<T>>;

/**
 * Polymorphic text primitive. Renders any element via `as`, applies a
 * typography variant, and forwards the rest of the element's native props.
 *
 * ```tsx
 * <Text as="h2" variant="title">Heading</Text>
 * <Text>Body copy</Text>
 * <Text as="span" variant="caption">Small print</Text>
 * ```
 */
const Text = <T extends ElementType = "p">({
  as,
  variant = "body",
  className = "",
  children,
  ...rest
}: TextProps<T>) => {
  const Component = (as ?? "p") as ElementType;

  return (
    <Component className={`text--${variant} ${className}`.trim()} {...rest}>
      {children}
    </Component>
  );
};

export default Text;
